import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowTopRightOnSquareIcon,
  NewspaperIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { updatesApi } from "../api/cmsApi";
import WysiwygEditor from "../components/WysiwygEditor";
import toast from "react-hot-toast";

interface UpdatePost {
  _id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  imageUrl: string | null;
  videoUrl: string | null;
  wistiaId: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  order?: number;
  createdAt: string;
}

interface CreateUpdatePayload {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  publishedAt?: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  wistiaId: string | null;
}

const categories = [
  { id: "news", label: "Neuigkeiten" },
  { id: "milestone", label: "Meilenstein" },
  { id: "video", label: "Video" },
  { id: "legal", label: "Rechtliches" },
];

const PREVIEW_BASE_URL = "https://claroclaim-amzn-app-preview.rapid-wind-5c78.workers.dev";

export default function Updates() {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState<UpdatePost | null>(null);

  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    excerpt: "",
    content: "",
    category: "news",
    publishedAt: "",
    imageUrl: "",
    videoUrl: "",
    wistiaId: "",
  });

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ["updates"],
    queryFn: () => updatesApi.list().then((res) => res.data.data || []),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUpdatePayload) => updatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["updates"] });
      setIsEditing(false);
      resetForm();
      toast.success("Beitrag erstellt");
    },
    onError: () => toast.error("Fehler beim Erstellen"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UpdatePost> }) =>
      updatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["updates"] });
      setIsEditing(false);
      setEditingPost(null);
      resetForm();
      toast.success("Beitrag aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => updatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["updates"] });
      toast.success("Beitrag gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, publish }: { id: string; publish: boolean }) =>
      updatesApi.publish(id, publish),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["updates"] });
      toast.success("Status aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const reorderMutation = useMutation({
    mutationFn: (items: { id: string; order: number }[]) =>
      updatesApi.reorder(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["updates"] });
    },
    onError: () => toast.error("Fehler beim Sortieren"),
  });

  const resetForm = () => {
    setFormData({
      slug: "",
      title: "",
      excerpt: "",
      content: "",
      category: "news",
      publishedAt: "",
      imageUrl: "",
      videoUrl: "",
      wistiaId: "",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      publishedAt: formData.publishedAt || null,
      imageUrl: formData.imageUrl || null,
      videoUrl: formData.videoUrl || null,
      wistiaId: formData.wistiaId || null,
    };
    if (editingPost) {
      updateMutation.mutate({ id: editingPost._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const startEditing = (post: UpdatePost) => {
    setEditingPost(post);
    setFormData({
      slug: post.slug,
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      publishedAt: post.publishedAt ? new Date(post.publishedAt).toISOString().split("T")[0] : "",
      imageUrl: post.imageUrl || "",
      videoUrl: post.videoUrl || "",
      wistiaId: post.wistiaId || "",
    });
    setIsEditing(true);
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[äöüß]/g, (char) => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[char] || char))
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    const items = [...updates] as UpdatePost[];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    const reorderItems = items.map((item, i) => ({ id: item._id, order: i }));
    reorderMutation.mutate(reorderItems);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <NewspaperIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Aktuelles</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie Blog-Posts und Updates für die Aktuelles-Seite
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsEditing(true);
            setEditingPost(null);
            resetForm();
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Neuer Beitrag
        </button>
      </div>

      {/* Edit Form */}
      {isEditing && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <form onSubmit={handleSubmit}>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingPost ? "Beitrag bearbeiten" : "Neuer Beitrag"}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        title: e.target.value,
                        slug: editingPost ? formData.slug : generateSlug(e.target.value),
                      });
                    }}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug (URL)</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    pattern="[a-z0-9-]+"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kategorie</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Veröffentlichungsdatum</label>
                  <input
                    type="date"
                    value={formData.publishedAt}
                    onChange={(e) => setFormData({ ...formData, publishedAt: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kurzbeschreibung</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  rows={2}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Inhalt</label>
                <WysiwygEditor
                  content={formData.content}
                  onChange={(html) => setFormData({ ...formData, content: html })}
                  placeholder="Beitrag verfassen..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bild-URL (optional)</label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Wistia ID (optional)</label>
                  <input
                    type="text"
                    value={formData.wistiaId}
                    onChange={(e) => setFormData({ ...formData, wistiaId: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="abc123xyz"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Video-URL (optional)</label>
                  <input
                    type="url"
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="https://youtube.com/..."
                  />
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingPost(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending ? "Speichern..." : "Speichern"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Posts List */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {isLoading ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">Laden...</div>
          ) : updates.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              Keine Beiträge vorhanden.
            </div>
          ) : (
            updates.map((post: UpdatePost, index: number) => (
              <div key={post._id} className="p-4 flex items-center justify-between">
                {/* Reorder arrows */}
                <div className="flex flex-col mr-3">
                  <button
                    onClick={() => moveItem(index, "up")}
                    disabled={index === 0 || reorderMutation.isPending}
                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Nach oben"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => moveItem(index, "down")}
                    disabled={index === updates.length - 1 || reorderMutation.isPending}
                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Nach unten"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">
                      {post.title}
                    </h3>
                    <span
                      className={`
                        px-2 py-0.5 rounded-full text-xs font-medium
                        ${post.isPublished
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                          : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                        }
                      `}
                    >
                      {post.isPublished ? "Veröffentlicht" : "Entwurf"}
                    </span>
                    <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full text-xs">
                      {categories.find((c) => c.id === post.category)?.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{post.excerpt}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    /{post.slug} • {post.publishedAt ? formatDate(post.publishedAt) : formatDate(post.createdAt)}
                  </p>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() =>
                      publishMutation.mutate({
                        id: post._id,
                        publish: !post.isPublished,
                      })
                    }
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title={post.isPublished ? "Verbergen" : "Veröffentlichen"}
                  >
                    {post.isPublished ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                  <a
                    href={`${PREVIEW_BASE_URL}/de/updates#${post.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Vorschau öffnen"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => startEditing(post)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm("Beitrag wirklich löschen?")) {
                        deleteMutation.mutate(post._id);
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
